import { Link, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Linkedin, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { SITE, detectLangFromPath, path } from "@/lib/site";
import { openCookiePreferences } from "@/components/CookieConsent";
import { useNavMenu } from "@/lib/useNavMenu";
import { useHeaderCta } from "@/lib/useHeaderCta";

const COOKIES_PATH = { fr: "/cookies", en: "/en/cookies", nl: "/nl/cookies" } as const;
const LEGAL_PATH = { fr: "/mentions-legales", en: "/en/legal", nl: "/nl/wettelijke-vermeldingen" } as const;
const PRIVACY_PATH = { fr: "/confidentialite", en: "/en/privacy", nl: "/nl/privacy" } as const;
const CGV_PATH = { fr: "/cgv", en: "/en/terms", nl: "/nl/algemene-voorwaarden" } as const;
const CGV_LABEL = { fr: "CGV", en: "Terms of sale", nl: "Algemene voorwaarden" } as const;

export function Footer() {
  const location = useLocation();
  const lang = detectLangFromPath(location.pathname);
  const { t } = useTranslation();
  const year = 2025;
  const { data: footerItems } = useNavMenu("footer", lang);
  const { data: cta } = useHeaderCta(lang, t("common.bookCall"));
  const ctaLabel = cta?.label ?? t("common.bookCall");
  const ctaHref = cta?.href ?? path(lang, "contact");
  const ctaExternal = cta?.isExternal ?? false;
  const ctaNewTab = cta?.openInNewTab ?? false;

  // Group footer items by groupLabel (fallback "Navigation")
  const fallbackGroup = t("footer.nav");
  const groups: { label: string; items: typeof footerItems }[] = [];
  if (footerItems && footerItems.length > 0) {
    const map = new Map<string, typeof footerItems>();
    for (const it of footerItems) {
      const key = it.groupLabel || fallbackGroup;
      const arr = map.get(key) ?? [];
      arr.push(it);
      map.set(key, arr);
    }
    for (const [label, items] of map.entries()) groups.push({ label, items });
  }


  return (
    <footer className="mt-24 border-t border-[var(--border-strong)] bg-background">
      <div className="container-page grid gap-12 py-16 md:grid-cols-12 md:py-20">
        <div className="md:col-span-5">
          <Logo />
          <p className="mt-6 max-w-sm text-[15px] leading-relaxed text-foreground/70">
            {t("footer.pitch")}
          </p>
          <a
            href={SITE.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-[var(--border-strong)] px-4 py-2 text-xs font-medium uppercase tracking-[0.14em] text-foreground/80 transition hover:border-foreground hover:text-foreground"
            aria-label="LinkedIn"
          >
            <Linkedin size={14} /> LinkedIn
          </a>
        </div>

        <div className="md:col-span-3">
          {groups.length > 0 ? (
            <div className="space-y-6">
              {groups.map((g) => (
                <div key={g.label}>
                  <p className="eyebrow mb-5">{g.label}</p>
                  <ul className="space-y-3 text-[15px]">
                    {(g.items ?? []).map((it) =>
                      it.isExternal ? (
                        <li key={it.id}>
                          <a
                            href={it.href}
                            target={it.openInNewTab ? "_blank" : undefined}
                            rel={it.openInNewTab ? "noopener noreferrer" : undefined}
                            className="text-foreground/75 transition hover:text-foreground"
                          >
                            {it.label}
                          </a>
                        </li>
                      ) : (
                        <li key={it.id}>
                          <Link
                            to={it.href}
                            target={it.openInNewTab ? "_blank" : undefined}
                            className="text-foreground/75 transition hover:text-foreground"
                          >
                            {it.label}
                          </Link>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <>
              <p className="eyebrow mb-5">{t("footer.nav")}</p>
              <ul className="space-y-3 text-[15px]">
                {(["services", "references", "about", "contact"] as const).map((p) => (
                  <li key={p}>
                    <Link
                      to={path(lang, p)}
                      className="text-foreground/75 transition hover:text-foreground"
                    >
                      {t(`nav.${p}`)}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="md:col-span-4">
          <p className="eyebrow mb-5">{t("footer.contact")}</p>
          <ul className="space-y-3 text-[15px]">
            <li>
              <a
                href={`mailto:${SITE.email}`}
                className="inline-flex items-center gap-2.5 text-foreground/75 hover:text-foreground"
              >
                <Mail size={14} /> {SITE.email}
              </a>
            </li>
            <li>
              <a
                href={`tel:${SITE.phoneRaw}`}
                className="inline-flex items-center gap-2.5 text-foreground/75 hover:text-foreground"
              >
                <Phone size={14} /> {SITE.phone}
              </a>
            </li>
            <li>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(
                  `${SITE.address.street}, ${SITE.address.postal} ${SITE.address.city}`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-start gap-2.5 text-foreground/75 hover:text-foreground"
              >
                <MapPin size={14} className="mt-1 shrink-0" />
                <span>
                  {SITE.address.street}
                  <br />
                  {SITE.address.postal} {SITE.address.city}, Belgique
                </span>
              </a>
            </li>
            <li className="pt-2">
              {ctaExternal ? (
                <a
                  href={ctaHref}
                  target={ctaNewTab ? "_blank" : undefined}
                  rel={ctaNewTab ? "noopener noreferrer" : undefined}
                  className="inline-flex items-center gap-1.5 text-foreground hover:gap-2.5 transition-all"
                >
                  <span className="font-display text-sm font-medium uppercase tracking-[0.1em]">
                    {ctaLabel}
                  </span>
                  <ArrowUpRight size={14} />
                </a>
              ) : (
                <Link
                  to={ctaHref}
                  target={ctaNewTab ? "_blank" : undefined}
                  className="inline-flex items-center gap-1.5 text-foreground hover:gap-2.5 transition-all"
                >
                  <span className="font-display text-sm font-medium uppercase tracking-[0.1em]">
                    {ctaLabel}
                  </span>
                  <ArrowUpRight size={14} />
                </Link>
              )}
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[var(--border)]">
        <div className="container-page flex flex-col items-start justify-between gap-3 py-6 text-xs text-foreground/70 sm:flex-row sm:items-center">
          <p>
            © {year} {SITE.name}. {t("footer.rights")}
          </p>
          <p className="sr-only">{t("geo")}</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to={LEGAL_PATH[lang]} className="transition hover:text-foreground">
              {t("footer.legal")}
            </Link>
            <Link to={PRIVACY_PATH[lang]} className="transition hover:text-foreground">
              {t("footer.privacy")}
            </Link>
            <Link
              to={COOKIES_PATH[lang]}
              className="transition hover:text-foreground"
            >
              {t("footer.cookies")}
            </Link>
            <Link to={CGV_PATH[lang]} className="transition hover:text-foreground">
              {CGV_LABEL[lang]}
            </Link>
            <button
              type="button"
              onClick={openCookiePreferences}
              className="transition hover:text-foreground"
            >
              {t("cookies.banner.customize")}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
